import { useVillageSatelliteTimeline } from "../hooks/useVillageSatelliteTimeline";
import { useVillageSatellitePanelActions } from "../hooks/useVillageSatellitePanelActions";
import type { VillageSatelliteBeforeAfterSectionProps } from "./VillageSatelliteBeforeAfterSection.types";

export function VillageSatelliteBeforeAfterSection({ model, villageName }: VillageSatelliteBeforeAfterSectionProps) {
  const timeline = useVillageSatelliteTimeline({
    availableYears: model.availableYears,
    leftYear: model.leftYear,
    rightYear: model.rightYear,
    selectLeftYear: model.selectLeftYear,
    selectRightYear: model.selectRightYear,
  });

  const panelActions = useVillageSatellitePanelActions({
    villageName,
    satelliteItems: model.satelliteItems,
  });

  return (
    <>
      {panelActions.isFullscreen ? <div className="satellite-before-after-overlay" onClick={panelActions.closeFullscreen} aria-hidden="true" /> : null}
      <article className={`box satellite-before-after-box fade-in${panelActions.isFullscreen ? " is-fullscreen" : ""}`}>
        <div className="box-title-row satellite-before-after-box__heading">
          <h3>Satellite Imagery</h3>
          <span className="satellite-before-after-box__ack">(Google Earth, Airbus)</span>
          <div className="satellite-before-after-box__actions">
            <button
              type="button"
              className="satellite-before-after-box__icon-button"
              onClick={panelActions.downloadAll}
              disabled={!panelActions.canDownload}
              aria-label={panelActions.isDownloading ? "Preparing satellite image zip" : "Download all satellite images"}
              title={panelActions.isDownloading ? "Preparing zip..." : "Download all satellite images"}
            >
              <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true">
                <path d="M12 3v11m0 0l-4-4m4 4l4-4M5 18v3h14v-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className="satellite-before-after-box__icon-button"
              onClick={panelActions.toggleFullscreen}
              aria-label={panelActions.isFullscreen ? "Exit full screen" : "View full screen"}
              title={panelActions.isFullscreen ? "Exit full screen" : "View full screen"}
            >
              {panelActions.isFullscreen ? (
                <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true">
                  <path d="M4 4l5 5M9 4v5H4M20 4l-5 5M15 4v5h5M4 20l5-5M9 20v-5H4M20 20l-5-5M15 20v-5h5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true">
                  <path d="M9 4H4v5M15 4h5v5M20 15v5h-5M4 15v5h5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
        </div>
        <div className="satellite-before-after-box__body">
          {model.hasComparisonData ? (
            <>
              <div
                className="satellite-year-timeline"
                aria-label="Satellite year timeline"
                ref={timeline.timelineRef}
                onPointerDown={timeline.onTimelinePointerDown}
              >
                <div className="satellite-year-timeline__rail" />
                <div
                  className="satellite-year-timeline__segment"
                  style={{ left: `${timeline.leftPercent}%`, right: `${100 - timeline.rightPercent}%` }}
                />

                <div
                  className={`satellite-year-timeline__marker is-left${timeline.dragTarget === "left" ? " is-dragging" : ""}`}
                  style={{ left: `${timeline.leftPercent}%` }}
                  onPointerDown={(event) => timeline.onMarkerPointerDown("left", event)}
                  role="slider"
                  aria-label="Left year marker"
                  aria-valuemin={0}
                  aria-valuemax={Math.max(0, model.availableYears.length - 1)}
                  aria-valuenow={timeline.leftIndex}
                >
                  L
                </div>
                <div
                  className={`satellite-year-timeline__marker is-right${timeline.dragTarget === "right" ? " is-dragging" : ""}`}
                  style={{ left: `${timeline.rightPercent}%` }}
                  onPointerDown={(event) => timeline.onMarkerPointerDown("right", event)}
                  role="slider"
                  aria-label="Right year marker"
                  aria-valuemin={0}
                  aria-valuemax={Math.max(0, model.availableYears.length - 1)}
                  aria-valuenow={timeline.rightIndex}
                >
                  R
                </div>

                <div className="satellite-year-timeline__labels">
                  {timeline.labels.map((item) => {
                    return (
                      <span
                        key={item.year}
                        className={`satellite-year-timeline__label${item.isLeft ? " is-left" : ""}${item.isRight ? " is-right" : ""}`}
                        style={{ left: `${item.percent}%` }}
                      >
                        {item.year}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="satellite-reveal-stage">
                <div className="satellite-reveal-layer satellite-reveal-layer--right">
                  {model.rightImageUrl ? (
                    <img src={model.rightImageUrl} alt={model.rightYear != null ? `Satellite image ${model.rightYear}` : "Satellite image"} loading="lazy" />
                  ) : (
                    <div className="satellite-reveal-empty" />
                  )}
                </div>

                <div
                  className="satellite-reveal-layer satellite-reveal-layer--left"
                  style={{ clipPath: `inset(0 ${100 - model.sliderPercent}% 0 0)` }}
                >
                  {model.leftImageUrl ? (
                    <img src={model.leftImageUrl} alt={model.leftYear != null ? `Satellite image ${model.leftYear}` : "Satellite image"} loading="lazy" />
                  ) : (
                    <div className="satellite-reveal-empty" />
                  )}
                </div>

                <div className="satellite-reveal-divider" style={{ left: `${model.sliderPercent}%` }}>
                  <span className="satellite-reveal-divider__handle" aria-hidden="true" />
                </div>

                <input
                  className="satellite-reveal-slider"
                  type="range"
                  min={0}
                  max={100}
                  value={model.sliderPercent}
                  onChange={(event) => model.setSliderPercent(Number(event.target.value))}
                  aria-label="Adjust before and after reveal"
                />

                {model.isPanelLoading ? (
                  <div className="satellite-reveal-loader" role="status" aria-live="polite" aria-label="Loading satellite imagery">
                    <span className="village-inline-loader__spinner" aria-hidden="true" />
                  </div>
                ) : null}
              </div>
            </>
          ) : model.isMetadataLoading ? (
            <div className="village-inline-loader" role="status" aria-live="polite" aria-label="Loading satellite imagery years">
              <span className="village-inline-loader__spinner" aria-hidden="true" />
            </div>
          ) : model.hasSatelliteData ? (
            <p className="small-paragraph">Need at least two years of satellite imagery for comparison.</p>
          ) : (
            <p className="small-paragraph">No satellite imagery is available for this village yet.</p>
          )}
        </div>
      </article>
    </>
  );
}
